/**
 * GlPage 總帳 (S8.1) — Board D #8, 會計 Finance nav.
 *
 * Split out of the codex `FinancePages.tsx`. The GL BUSINESS surface is
 * placeholders only (R217 — all account fields ship day-one as placeholders,
 * GL posting/wiring deferred): the 會計科目 chart of accounts and the 傳票
 * journal records that the demo chain already wrote (linked to their AR/AP
 * sources).
 *
 * The GL 展示數據 fixture card (P&L / balance sheet pretending a GL engine
 * computed them) and the EnginesPages costing fixture viewer do NOT enter
 * this business surface: both are gated behind the DevFlagPanel dev flag
 * (`import.meta.env.DEV` + `engines.costing`, OFF by default and absent from
 * the demo profile) and retire at W9 — holistic plan §S8.1 / Salvage verdict.
 *
 * S0.5 followUp check (verified by vitest apPayActualSupply.test.ts): the GL
 * fixture account formerly numbered 1200 with the banned on-hand label is
 * renamed — the seed carries 1250 "Goods in transit" (direct-ship narrative).
 */

import { Alert, Button, Card, Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { ExperimentOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRequest } from 'ahooks';
import PageHeader from '@/components/common/PageHeader';
import TableContainer, { renderDash } from '@/components/common/TableContainer';
import { useFeatureFlags } from '@/features/flags';
import apiClient from '@/services/api/axios.config';
import { controlHeightTokens } from '@/styles/tokens';
import type {
  ChartOfAccount,
  GlStatementRow,
  GlStatementsResponse,
  JournalEntry,
} from '@/types/api/finance';
import { formatAmount } from '../salesOrders/components/soFormat';

const { Text } = Typography;

const glApi = {
  chartOfAccounts: async (): Promise<{ data: ChartOfAccount[] }> => {
    const { data } = await apiClient.get<{ data: ChartOfAccount[] }>(
      '/finance/chart-of-accounts'
    );
    return data;
  },
  journals: async (): Promise<{ data: JournalEntry[] }> => {
    const { data } = await apiClient.get<{ data: JournalEntry[] }>('/finance/journals');
    return data;
  },
  statements: async (): Promise<{ data: GlStatementsResponse }> => {
    const { data } = await apiClient.get<{ data: GlStatementsResponse }>(
      '/finance/gl-statements'
    );
    return data;
  },
};

const ACCOUNT_TYPE_COLOR: Record<ChartOfAccount['type'], string> = {
  asset: 'blue',
  liability: 'orange',
  equity: 'purple',
  revenue: 'green',
  expense: 'red',
};

const JOURNAL_STATUS_COLOR: Record<JournalEntry['status'], string> = {
  draft: 'default',
  posted: 'green',
  reversed: 'red',
};

/** Dev-only 展示數據 card (DevFlagPanel · engines.costing — retires at W9). */
const DevFixturesCard = () => {
  const { t } = useTranslation(['finance', 'common']);
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { data, loading } = useRequest(async () => (await glApi.statements()).data, {
    cacheKey: '/api/finance/gl-statements',
  });

  const statementColumns: TableProps<GlStatementRow>['columns'] = [
    {
      title: t('finance:gl.devFixtures.fields.account'),
      dataIndex: 'account',
      key: 'account',
      width: 100,
    },
    { title: t('finance:gl.devFixtures.fields.label'), dataIndex: 'label', key: 'label' },
    {
      title: t('finance:gl.devFixtures.fields.amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      width: 140,
      render: (value: number) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(value)}</span>
      ),
    },
  ];

  return (
    <Card
      title={
        <span>
          <ExperimentOutlined style={{ color: token.colorPrimary, marginRight: 8 }} />
          {t('finance:gl.devFixtures.title')}
        </span>
      }
      style={{ marginTop: token.marginLG }}
      extra={
        <Button
          size="large"
          style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          onClick={() => navigate('/auth/engines/costing')}
        >
          {t('finance:gl.devFixtures.costingLink')}
        </Button>
      }
    >
      <Alert
        type="warning"
        showIcon
        message={t('finance:gl.devFixtures.body')}
        style={{ marginBottom: token.marginMD }}
      />
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            {t('finance:gl.devFixtures.pnl')}
            {data?.period ? ` — ${data.period}` : ''}
          </Text>
          <Table<GlStatementRow>
            size="small"
            rowKey="account"
            loading={loading}
            dataSource={data?.profitAndLoss ?? []}
            columns={statementColumns}
            pagination={false}
          />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            {t('finance:gl.devFixtures.bs')}
          </Text>
          <Table<GlStatementRow>
            size="small"
            rowKey="account"
            loading={loading}
            dataSource={data?.balanceSheet ?? []}
            columns={statementColumns}
            pagination={false}
          />
        </div>
      </div>
    </Card>
  );
};

const GlPage = () => {
  const { t } = useTranslation(['finance', 'common']);
  const { token } = theme.useToken();
  const { flags } = useFeatureFlags();

  const { data: accounts, loading: accountsLoading } = useRequest(
    async () => (await glApi.chartOfAccounts()).data,
    { cacheKey: '/api/finance/chart-of-accounts' }
  );
  const { data: journals, loading: journalsLoading } = useRequest(
    async () => (await glApi.journals()).data,
    { cacheKey: '/api/finance/journals' }
  );

  const coaColumns: TableProps<ChartOfAccount>['columns'] = [
    {
      title: t('finance:gl.coa.fields.code'),
      dataIndex: 'code',
      key: 'code',
      width: 120,
      render: (value: string) => <span style={{ fontWeight: token.fontWeightStrong }}>{value}</span>,
    },
    { title: t('finance:gl.coa.fields.name'), dataIndex: 'name', key: 'name' },
    {
      title: t('finance:gl.coa.fields.type'),
      dataIndex: 'type',
      key: 'type',
      width: 130,
      render: (value: ChartOfAccount['type']) => (
        <Tag color={ACCOUNT_TYPE_COLOR[value]} style={{ margin: 0 }}>
          {t(`finance:gl.accountType.${value}`)}
        </Tag>
      ),
    },
  ];

  const journalColumns: TableProps<JournalEntry>['columns'] = [
    {
      title: t('finance:gl.journals.fields.number'),
      dataIndex: 'journalNumber',
      key: 'journalNumber',
      width: 160,
      render: (value: string) => <span style={{ fontWeight: token.fontWeightStrong }}>{value}</span>,
    },
    { title: t('finance:gl.journals.fields.date'), dataIndex: 'date', key: 'date', width: 130 },
    {
      title: t('finance:gl.journals.fields.source'),
      dataIndex: 'sourceType',
      key: 'sourceType',
      width: 120,
      render: (value: string) => <Tag style={{ margin: 0 }}>{value}</Tag>,
    },
    {
      title: t('finance:gl.journals.fields.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value: JournalEntry['status']) => (
        <Tag color={JOURNAL_STATUS_COLOR[value]} style={{ margin: 0 }}>
          {t(`finance:gl.journalStatus.${value}`)}
        </Tag>
      ),
    },
  ];

  return (
    <div className="tw-px-6 tw-pb-8">
      <PageHeader title={t('finance:gl.title')} />

      {/* R217 — GL wiring deferred; account refs are day-one placeholders */}
      <Alert
        type="info"
        showIcon
        message={t('finance:gl.deferredTitle')}
        description={t('finance:gl.deferredBody')}
        style={{ marginBottom: token.marginLG }}
      />

      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: token.fontSizeLG }}>
        {t('finance:gl.coa.title')}
      </Text>
      <TableContainer>
        <Table<ChartOfAccount>
          rowKey="code"
          loading={accountsLoading}
          dataSource={accounts ?? []}
          columns={coaColumns}
          pagination={false}
        />
      </TableContainer>

      <Text
        strong
        style={{
          display: 'block',
          marginTop: token.marginLG,
          marginBottom: 8,
          fontSize: token.fontSizeLG,
        }}
      >
        {t('finance:gl.journals.title')}
      </Text>
      <TableContainer>
        <Table<JournalEntry>
          rowKey="id"
          loading={journalsLoading}
          dataSource={journals ?? []}
          columns={journalColumns}
          pagination={false}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                size="small"
                rowKey={(line) => `${record.id}-${line.account}-${line.memo}`}
                dataSource={record.lines}
                pagination={false}
                columns={[
                  {
                    title: t('finance:gl.journals.fields.account'),
                    dataIndex: 'account',
                    key: 'account',
                    width: 120,
                  },
                  {
                    title: t('finance:gl.journals.fields.debit'),
                    dataIndex: 'debit',
                    key: 'debit',
                    align: 'right',
                    width: 140,
                    render: (value: number) =>
                      value ? (
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatAmount(value)}
                        </span>
                      ) : (
                        renderDash()
                      ),
                  },
                  {
                    title: t('finance:gl.journals.fields.credit'),
                    dataIndex: 'credit',
                    key: 'credit',
                    align: 'right',
                    width: 140,
                    render: (value: number) =>
                      value ? (
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatAmount(value)}
                        </span>
                      ) : (
                        renderDash()
                      ),
                  },
                  {
                    title: t('finance:gl.journals.fields.memo'),
                    dataIndex: 'memo',
                    key: 'memo',
                  },
                ]}
              />
            ),
          }}
        />
      </TableContainer>

      {/* GL 展示數據 + costing fixture viewer — DevFlagPanel-gated ONLY
          (never on the business surface; retires at W9). */}
      {import.meta.env.DEV && flags['engines.costing'] && <DevFixturesCard />}
    </div>
  );
};

export default GlPage;
